import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Loader2, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "pa24h_chat_sid";

const getSessionId = () => {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
};

const WELCOME = {
  role: "assistant",
  content: "¡Hola! Soy el asistente de Piezas Aljarafe 24H. Cuéntame qué le pasa a tu coche y te ayudo a identificar la pieza que necesitas.",
};

const chips = [
  "Tengo un ruido al frenar",
  "Se enciende un testigo del motor",
  "Necesito un faro o piloto",
];

export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: snapshot };
          return next;
        });
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: "Ahora mismo no puedo responder. Escríbenos por WhatsApp al 644 856 072 y te ayudamos al momento." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-chat-button"
        aria-label="Abrir asistente IA"
        className="fixed bottom-6 left-6 z-50 group"
      >
        <span className="absolute inset-0 rounded-full bg-brand-orange animate-ping opacity-20" />
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-brand-orange text-white shadow-lg shadow-brand-orange/30 transition-transform duration-300 group-hover:scale-110">
          {open ? <X className="w-6 h-6" /> : <Bot className="w-7 h-7" />}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 left-4 sm:left-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[30rem] bg-industrial-900 border border-industrial-700 rounded-md shadow-2xl flex flex-col overflow-hidden"
            data-testid="ai-chat-panel"
          >
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-industrial-700 bg-industrial-850">
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-brand-orange/15 text-brand-amber">
                <Bot className="w-5 h-5" />
              </span>
              <div>
                <p className="font-display font-bold uppercase tracking-wider text-sm">Asistente IA</p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Te ayudo a identificar tu pieza</p>
              </div>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-md text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-brand-orange text-white"
                        : "bg-industrial-800 text-slate-200 border border-industrial-700"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-brand-amber" /> : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {chips.map((c, i) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid={`ai-chat-chip-${i}`}
                      className="font-mono text-[10px] uppercase tracking-widest border border-industrial-600 hover:border-brand-amber text-slate-400 hover:text-brand-amber px-3 py-2 rounded-sm transition-colors"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 p-3 border-t border-industrial-700 bg-industrial-850"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="ai-chat-input"
                placeholder="Describe la avería o la pieza..."
                maxLength={1000}
                className="flex-1 bg-industrial-900 border border-industrial-700 rounded-sm px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-brand-amber"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="ai-chat-send-button"
                className="flex items-center justify-center w-10 h-10 rounded-sm bg-brand-orange hover:bg-brand-amber disabled:opacity-50 text-white transition-colors"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
